import React from "react";
import { Link } from "react-router-dom";
import { Tv, Settings, UserCog, ArrowUpRight, Shield, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const Card = ({ to, title, subtitle, icon: Icon, testId }) => (
  <Link
    to={to}
    className="group relative flex flex-col justify-between border border-border hover:border-foreground transition-colors duration-300 bg-background p-8 md:p-10 min-h-[240px]"
    data-testid={testId}
  >
    <div className="flex items-start justify-between">
      <Icon className="w-8 h-8" strokeWidth={1.5} />
      <ArrowUpRight className="w-6 h-6 opacity-30 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
    </div>
    <div>
      <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-3">{subtitle}</div>
      <div className="font-heading text-3xl md:text-4xl font-black tracking-tighter">{title}</div>
    </div>
  </Link>
);

const Home = () => {
  const { user, company, logout } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-foreground" />
            <span className="font-heading font-bold tracking-tighter text-xl">
              {company?.name || "FILA INTELIGENTE"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <span className="text-xs uppercase tracking-[0.25em] text-foreground/60 hidden md:inline">
                  {user.name} · {user.role === "super_admin" ? "Plataforma" : user.role === "owner" ? "Dono" : "Operador"}
                </span>
                <Button variant="outline" size="sm" className="rounded-none" onClick={logout} data-testid="home-logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Sair
                </Button>
              </>
            ) : (
              <Link to="/login">
                <Button size="sm" className="rounded-none" data-testid="home-login-btn">
                  Entrar
                </Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-8">
            <div className="text-xs uppercase tracking-[0.3em] text-foreground/60 mb-6">
              Plataforma SaaS · Gerenciamento de Filas
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tighter leading-[0.9]">
              Chamada de senhas com identidade visual
              <span className="text-foreground/30"> total.</span>
            </h1>
          </div>
          <div className="col-span-12 md:col-span-4 md:pt-8">
            <p className="text-base md:text-lg text-foreground/70 leading-relaxed">
              Display público, painel administrativo customizável e console do operador
              sincronizados em tempo real entre dispositivos. Multi-empresa com dados isolados
              em backend.
            </p>
          </div>
        </div>

        {!user && (
          <div className="border border-foreground p-8 md:p-12 mb-12">
            <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-3">
              Comece agora
            </div>
            <div className="font-heading text-3xl md:text-4xl font-black tracking-tighter mb-6">
              Faça login para acessar sua empresa
            </div>
            <Link to="/login">
              <Button className="rounded-none h-12 px-8 text-sm uppercase tracking-[0.2em]" data-testid="home-hero-login-btn">
                Entrar na Plataforma
              </Button>
            </Link>
          </div>
        )}

        {user?.role === "super_admin" && (
          <div className="grid grid-cols-1 gap-0 border-t border-l border-border [&>*]:border-r [&>*]:border-b [&>*]:border-border [&>*]:!border-l-0 [&>*]:!border-t-0">
            <Card
              to="/super-admin"
              icon={Shield}
              subtitle="Plataforma"
              title="Gerenciar Empresas"
              testId="home-super-admin-card"
            />
          </div>
        )}

        {user && user.role !== "super_admin" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-border [&>*]:border-r [&>*]:border-b [&>*]:border-border [&>*]:!border-l-0 [&>*]:!border-t-0">
            <Card to="/tv" icon={Tv} subtitle="Display Público" title="Tela TV" testId="home-tv-card" />
            <Card to="/operator" icon={UserCog} subtitle="Console" title="Operador" testId="home-operator-card" />
            {user.role === "owner" && (
              <Card to="/admin" icon={Settings} subtitle="Configuração" title="Admin" testId="home-admin-card" />
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Home;
